import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

export default function Termin() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const preAction = params.get("aktion");

  const [booking, setBooking] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | invalid | done
  const [result, setResult] = useState(null); // 'accepted' | 'rejected'
  const [showReason, setShowReason] = useState(preAction === "ablehnen");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bookings/${id}/details`, { params: { token } });
        setBooking(data);
        if (data.status === "accepted" || data.status === "rejected") {
          setResult(data.status);
          setState("done");
        } else {
          setState("ready");
        }
      } catch {
        setState("invalid");
      }
    })();
  }, [id, token]);

  const decide = async (action) => {
    if (action === "ablehnen" && !showReason) { setShowReason(true); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${id}/decision`, { token, action, reason });
      setResult(data.status);
      setState("done");
      toast.success(action === "annehmen" ? "Termin bestätigt – Kundin wurde informiert." : "Termin abgelehnt – Kundin wurde informiert.");
    } catch {
      toast.error("Aktion fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="termin-page" className="min-h-screen pt-32 pb-24 flex items-start justify-center">
      <div className="w-full max-w-lg px-5 md:px-10">
        <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Terminanfrage · nur für dich</span>
        <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight mt-4 mb-8">Termin verwalten</h1>

        {state === "loading" && (
          <div className="flex items-center gap-3 text-foreground/60"><Loader2 className="animate-spin" size={20} /> Lädt…</div>
        )}

        {state === "invalid" && (
          <div data-testid="termin-invalid" className="border border-border p-8 text-foreground/70">
            Dieser Link ist ungültig oder abgelaufen.
          </div>
        )}

        {(state === "ready" || state === "done") && booking && (
          <div className="border border-border p-8">
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-6"><dt className="text-muted-foreground">Kundin</dt><dd className="text-foreground/90 text-right">{booking.name}</dd></div>
              <div className="flex justify-between gap-6"><dt className="text-muted-foreground">E-Mail</dt><dd className="text-foreground/90 text-right">{booking.email}</dd></div>
              <div className="flex justify-between gap-6"><dt className="text-muted-foreground">Telefon</dt><dd className="text-foreground/90 text-right">{booking.phone}</dd></div>
              <div className="flex justify-between gap-6"><dt className="text-muted-foreground">Leistung</dt><dd className="text-foreground/90 text-right max-w-[60%]">{booking.service}{booking.price ? ` · ${booking.price}` : ""}</dd></div>
              <div className="flex justify-between gap-6"><dt className="text-muted-foreground">Termin</dt><dd className="text-foreground/90 text-right">{booking.date} · {booking.time} Uhr</dd></div>
              {booking.notes ? <div className="flex justify-between gap-6"><dt className="text-muted-foreground">Notizen</dt><dd className="text-foreground/90 text-right max-w-[60%]">{booking.notes}</dd></div> : null}
            </dl>

            {state === "done" ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease }} className="mt-8 pt-6 border-t border-border">
                {result === "accepted" ? (
                  <div data-testid="termin-result-accepted" className="flex items-center gap-3 text-primary">
                    <CheckCircle2 strokeWidth={1.5} /> <span className="font-serif text-2xl">Termin bestätigt</span>
                  </div>
                ) : (
                  <div data-testid="termin-result-rejected" className="flex items-center gap-3 text-foreground/70">
                    <XCircle strokeWidth={1.5} /> <span className="font-serif text-2xl">Termin abgelehnt</span>
                  </div>
                )}
                <p className="mt-3 text-sm text-muted-foreground">Die Kundin wurde automatisch per E-Mail benachrichtigt.</p>
              </motion.div>
            ) : (
              <div className="mt-8 pt-6 border-t border-border">
                {showReason && (
                  <div className="mb-4">
                    <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Grund (optional)</label>
                    <Textarea data-testid="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z. B. Zeitpunkt leider nicht verfügbar" className="mt-2 rounded-none bg-background border-border min-h-[90px]" />
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button data-testid="termin-accept" disabled={busy} onClick={() => decide("annehmen")} className="flex-1 bg-primary text-primary-foreground py-4 text-sm tracking-wide disabled:opacity-60">
                    Annehmen
                  </button>
                  <button data-testid="termin-reject" disabled={busy} onClick={() => decide("ablehnen")} className="flex-1 border border-foreground py-4 text-sm tracking-wide hover:bg-foreground hover:text-background transition-colors disabled:opacity-60">
                    {showReason ? "Ablehnung bestätigen" : "Ablehnen"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
